<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  defineEmits,
  defineExpose,
  watchEffect,
  nextTick,
} from 'vue';
import MInput from 'src/components/MInput/index.vue';
import { use18n, useIns } from 'src/utils/v2_helper/common';
import { emailReg } from 'src/utils/base';
import { useFillInfo } from './hooks.js';

const emit = defineEmits(['onInfo']);
const app = useIns();
const { $t } = use18n();
const userForm = ref({
  nickName: '',
  email: '',
});
const fieldRef1 = ref();
const fieldRef2 = ref();
const userSection = ref();

// 解构出用于补充用户信息表单的变量和方法
const { fillForm,
  fillFieldRefs,
  showFillForm,
  fillFormValid,
  fillFormInvalidQueue,
  formListConfig } = useFillInfo()

const hasInfo = computed(() => {
  const { email, nickName } = app.userInfo || {};
  return email || nickName;
});
const invalidEmailErrMsg = computed(() => {
  if (app.emailOptional && !userForm.value.email) return '';
  if (!userForm.value.email) return $t('checkout.requiredEmailTip');
  if (!emailReg.test(userForm.value.email)) {
    return $t('checkout.EmailInvalid');
  }
  return '';
});
const invalidNameErrMsg = computed(() => {
  if (!app.nameOptional && !userForm.value.nickName) return $t('checkout.requiredNameTip');
  return '';
});
const invalidQueue = computed(() => {
  const q = [];
  if (invalidNameErrMsg.value) {
    q.push({ msg: invalidNameErrMsg.value, el: fieldRef1.value.el });
  }
  if (invalidEmailErrMsg.value) {
    q.push({ msg: invalidEmailErrMsg.value, el: fieldRef2.value.el });
  }
  return q;
});
// AI-assisted 2026-09 START: Select the active validation state from the buyer type.
const userValid = computed(() => (
  app.userInfo.guestUser
    ? fillFormValid.value
    : invalidQueue.value.length === 0
));
const focusFirst = () => {
  const errorField = app.userInfo.guestUser
    ? fillFormInvalidQueue.value[0]
    : invalidQueue.value[0];
  if (!errorField) return;
  document.body.scrollTo(0, 0);
  errorField.el?.focus();
};
// AI-assisted 2026-09 END

// AI-assisted 2026-09 START: Focus an existing editable field after backend validation.
async function focusBuyerInfo(errorKey) {
  await nextTick();
  userSection.value?.scrollIntoView({ block: 'start' });
  const field = errorKey === 'BUYER_NAME_REQUIRED'
    ? 'nickName'
    : (errorKey === 'BUYER_EMAIL_REQUIRED' ? 'email' : 'phone');
  const input = app.userInfo.guestUser ? fillFieldRefs[field]?.value
    : (field === 'nickName' ? fieldRef1.value : (field === 'email' ? fieldRef2.value : null));
  input?.el?.focus();
}
// AI-assisted 2026-09 END

watch(
  () => (app.userInfo.guestUser ? fillForm.value : userForm.value),
  (value) => {
    emit('onInfo', value);
  },
  {
    deep: true,
    immediate: true,
  },
);
onMounted(() => {
  const { nickName, email } = app.userInfo || {};
  userForm.value = { nickName, email };
  // 自动回填上次填写的游客信息
  let guestUserInfo = localStorage.getItem('guestUser') || '{}';
  guestUserInfo = JSON.parse(guestUserInfo)
  // eslint-disable-next-line no-restricted-syntax
  for (const key in fillForm.value) {
    if (guestUserInfo[key]) {
      fillForm.value[key] = guestUserInfo[key]
    }
  }
});

defineExpose({
  focusFirst,
  focusBuyerInfo,
  userForm,
  userValid,
  fillForm,
  fillFormValid
});
</script>

<template>
  <section ref="userSection" class="user">
    <section v-if="hasInfo" class="user-info">
      <p class="user-info-tile">{{ $t('fullDinein.contactInfora') }}</p>
      <p v-if="userInfo.nickName" class="user-info-name">
        {{ userInfo.nickName }}
      </p>
      <p v-if="userInfo.phone" class="user-info-phone">{{ userInfo.phone }}</p>
    </section>
    <section v-if="!userInfo.guestUser && (!userInfo.nickName || !userInfo.email)" class="user-form">
      <div v-if="!userInfo.nickName" class="user-form-field">
        <MInput
          ref="fieldRef1"
          v-model="userForm.nickName"
          :required="!nameOptional"
          :max="254"
          :label="$t('fullDinein.name')"
          :placeholder="$t('checkout.namePlaceholder')"
          :err-msg="invalidNameErrMsg"
        />
      </div>
      <div v-if="!userInfo.email" class="user-form-field">
        <MInput
          ref="fieldRef2"
          v-model="userForm.email"
          :required="!emailOptional"
          :max="254"
          :label="$t(`checkout.Email${emailOptional ? 'Optnal' : ''}`)"
          :placeholder="$t('checkout.emailPlaceholder')"
          :err-msg="invalidEmailErrMsg"
        />
        <p class="field-tip">{{ $t('checkout.emailFieldTip') }}</p>
      </div>
    </section>
    <section v-if="showFillForm" class="user-form">
      <div v-for="item in formListConfig" :key="item.prop" class="fill-form-field">
        <MInput
          :ref="item.ref"
          v-model="fillForm[item.prop]"
          :required="item.required"
          :max="item.max"
          :placeholder="item.placeholder"
          :filter="item.filter"
          :label="item.label"
          autocomplete="on"
          :err-msg="item.errMsg.value"
        >
          <template #desc>
            <div v-if="item.prop === 'phone'" class="field-desc">
              <span>{{ $t('checkout.phoneDesc') }}</span>
              <a class="important" href="https://www.jointoday.co/register/bakmid3l8zj" target="_blank">{{ $t('checkout.phoneDescLink') }}</a>
            </div>
            <div v-else-if="item.prop === 'email'" class="field-desc">
             {{ $t('checkout.guestEmailFieldTip') }}
            </div>
          </template>
          <template v-if="item.prop === 'phone'" #prepend>
            <div class="prepend">
            +62
            </div>
          </template>
        </MInput>
      </div>
    </section>
  </section>
</template>

<style lang="scss" scoped>
*[red] {
  color: red;
}
.m-tile {
  color: Black;
  font-weight: bold;
  font-size: 12px;
}
.user {
  & > section {
    padding: 16px;
  }
  &-line {
    margin: 10px 0;
    border-top: solid 1px $grayBg;
  }
  &-form {
    &-field {
      & + & {
        margin-top: 16px;
      }
      display: flex;
      flex-direction: column;
      .field-label {
        @extend .m-tile;
        margin-bottom: 16px;
      }
      .field-input {
        height: 42px;
        background: #ffffff;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        outline: none;
        padding: 0 8px;
      }
      .field-tip {
        color: $grayFont;
        margin-top: 8px;
      }
    }
    :deep(.minput-label) {
      font-size: 12px;
    }
  }
  &-info {
    padding-bottom: 10px;
    p {
      margin: 0;
    }
    &-tile {
      @extend .m-tile;
      padding-bottom: 2px;
    }
    &-name {
      font-size: 16px;
    }
    &-phone {
      color: #6b7280;
      font-size: 12px;
    }
  }
}
.fill-form-field {
  margin-bottom: 16px;
}
.field-desc {
  color: #232323;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  margin-bottom: 16px;
  .important {
    color: #C63527;
    font-weight: 700;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-underline-position: from-font;
  }
}
.prepend {
  padding: 0 13px;
  height: 100%;
  background: $button-bg;
  align-self: stretch;
  display: flex;
  align-items: center;
  color: $grayFont;
  border-right: 1px solid $border;
  .countryIcon {
    font-size: 24px;
    margin-right: 2px;
  }
}
</style>
